import React, { useState, useEffect } from 'react';
import './styles.css';
import { getRecipeIngredientNamesWithTranslations, ezPairingApi } from '@xoxokiosk/shared';
import RadarChart from './components/RadarChart';

interface Question {
  id: string;
  question: string;
  questionEn: string;
  choices: Array<{
    id: string;
    text: string;
    textEn: string;
    emoji?: string;
  }>;
}

interface Recipe {
  id: string;
  name: string;
  image?: string;
  recipeImage?: { imageUrl: string };
  ingredients?: Array<{ name: string }>;
  glass?: { name: string };
  drinkFeatures?: Array<{ property: string; val: number }>;
  abv?: number;
  recipeIngredient?: Array<{
    name: string;
    ingredient?: { name: string };
  }>;
}

interface RecommendationWithExplanation {
  recipe: Recipe;
  // New bilingual explanation fields from server; keep legacy `explanation` for back-compat
  explanation?: string;
  explanationEn?: string;
  explanationZh?: string;
}

// Get LLM-based recommendations from backend
const getRecommendations = async (answers: Record<string, string>, shopId: string, recipes: Recipe[]): Promise<RecommendationWithExplanation[]> => {
  try {
    const response = await ezPairingApi.getRecommendations(shopId, answers);
    const recommendations = response.data?.recommendations || [];

    // Map recommendation IDs back to full recipe objects with bilingual explanations.
    // Be tolerant of older mappings that use `explanation` only.
    const recommendedRecipes = recommendations
      .map((r: any) => {
        const recipe = recipes.find(recipe => recipe.id === r.recipeId);
        if (!recipe) return null;
        const explanationEn = r.explanationEn || r.explanation || '';
        const explanationZh = r.explanationZh || r.explanation || '';
        return { recipe, explanation: r.explanation, explanationEn, explanationZh } as RecommendationWithExplanation;
      })
      .filter(Boolean) as RecommendationWithExplanation[];

    return recommendedRecipes.slice(0, 1);
  } catch (error) {
    console.error('Failed to get LLM recommendations, using fallback:', error);
    
    // Fallback to simple scoring if LLM fails
    return getFallbackRecommendations(answers, recipes);
  }
};

// Fallback recommendation logic
const getFallbackRecommendations = (answers: Record<string, string>, recipes: Recipe[]): RecommendationWithExplanation[] => {
  if (recipes.length === 0) return [];
  
  // Simple scoring system as fallback
  const scoreRecipe = (recipe: Recipe): number => {
    let score = 0;
    const features = recipe.drinkFeatures || [];
    
    // Basic scoring based on answers
    if (answers.taste === 'sweet') {
      score += (features.find(f => f.property === 'sweetness')?.val || 0) * 0.5;
    }
    if (answers.taste === 'sour') {
      score += (features.find(f => f.property === 'sourness')?.val || 0) * 0.5;
    }
    
    return score + Math.random() * 2;
  };
  
  const scoredRecipes = recipes.map(recipe => ({
    recipe,
    score: scoreRecipe(recipe)
  }));
  
  scoredRecipes.sort((a, b) => b.score - a.score);
  return scoredRecipes.slice(0, 1).map(item => ({ 
    recipe: item.recipe, 
    explanation: "A great choice based on your preferences!" 
  }));
};

interface EzPairingExperienceProps {
  instance: {
    join_code: string;
    shop_id: string;
    kiosk_type_identifier?: string;
  };
  shop: {
    shop_id: string;
    name: string;
  };
  recipes: Recipe[];
  config?: any;
  onOrderSubmit?: (order: any) => Promise<any>;
  onError?: (error: Error) => void;
}

const EzPairingExperience: React.FC<EzPairingExperienceProps> = ({ instance, shop, recipes: initialRecipes, onOrderSubmit }) => {
  console.log('[EZ-PAIRING] Component mounted with props:', { instance, shop, recipesCount: initialRecipes?.length });
  
  const [step, setStep] = useState(1); // 1: name entry, 2: questions, 3: recommendations
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [guestName, setGuestName] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [questions, setQuestions] = useState<Question[]>([]);
  const recipes = initialRecipes || [];
  const [recommendations, setRecommendations] = useState<RecommendationWithExplanation[]>([]);
  const [selectedDrink, setSelectedDrink] = useState<RecommendationWithExplanation | null>(null);
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [orderDisplayId, setOrderDisplayId] = useState<string | null>(null);
  const [questionsLoading, setQuestionsLoading] = useState<boolean>(true);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState<number>(0);
  const [demoSettings, setDemoSettings] = useState<{ demoMode: boolean; demoName?: string } | null>(null);
  const [appearance, setAppearance] = useState<{ titleText?: string; logoUrl?: string; backgroundUrl?: string; logoGradient?: boolean } | null>(null);
  const [showPreparation, setShowPreparation] = useState(false);

  // Load questions (recipes are passed as props)
  useEffect(() => {
    console.log('[EZ-PAIRING] useEffect triggered, shop:', shop);
    const loadData = async () => {
      setQuestionsLoading(true);
      setQuestionsError(null);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn('[EZ-PAIRING] Aborting questions fetch due to timeout');
        controller.abort();
      }, 10000); // 10s timeout

      try {
        console.log('[EZ-PAIRING] Loading questions for shop:', shop.shop_id);
        const questionsResponse = await ezPairingApi.getQuestions(shop.shop_id, { signal: controller.signal, timeout: 10000 });
        console.log('[EZ-PAIRING] Questions response received');
        const questionsData = questionsResponse.data;
        console.log('[EZ-PAIRING] Raw response data:', questionsData);
        const questionsToSet = Array.isArray(questionsData?.questions) ? questionsData.questions : [];
        if (questionsToSet.length === 0) {
          throw new Error('No questions configured for this shop.');
        }
        console.log('[EZ-PAIRING] Setting questions state with length:', questionsToSet.length);
        setQuestions(questionsToSet);
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          console.error('[EZ-PAIRING] Questions fetch aborted (timeout)');
          setQuestionsError('Request timed out. Please try again.');
        } else {
          console.error('[EZ-PAIRING] Failed to load questions:', error);
          setQuestionsError(error?.message || 'Failed to load questions.');
        }
        setQuestions([]);
      } finally {
        clearTimeout(timeoutId);
        setQuestionsLoading(false);
      }
    };
    
    if (shop?.shop_id) {
      console.log('[EZ-PAIRING] Shop ID available, loading data...');
      void loadData();
      void (async () => {
        try {
          if (!instance?.join_code) throw new Error('missing join_code');
          const resp = await (ezPairingApi as any).getDemoSettings(instance.join_code);
          setDemoSettings(resp.data || { demoMode: false });
        } catch (err) {
          console.warn('[EZ-PAIRING] No demo settings available', err);
          setDemoSettings({ demoMode: false });
        }
      })();
      // Read appearance from instance.config if present
      try {
        const rawCfg = (instance as any).config;
        let cfg: any = {};
        if (typeof rawCfg === 'string') {
          try { cfg = JSON.parse(rawCfg); } catch (e) { cfg = {}; }
        } else if (typeof rawCfg === 'object' && rawCfg !== null) {
          cfg = rawCfg;
        }
        setAppearance(cfg.appearance || null);
      } catch (e) {
        setAppearance(null);
      }
    } else {
      console.log('[EZ-PAIRING] No shop_id available, shop object:', shop);
      setQuestionsLoading(false);
      setQuestionsError('Missing shop ID.');
    }
  }, [shop?.shop_id, reloadToken]);

  // Track questions state changes
  useEffect(() => {
    console.log('[EZ-PAIRING] Questions state changed, new length:', questions.length);
    console.log('[EZ-PAIRING] Questions state content:', questions);
  }, [questions]);

  const handleAnswerSelect = async (choiceId: string) => {
    const newAnswers = { ...answers, [questions[currentQuestion].id]: choiceId };
    setAnswers(newAnswers);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // All questions answered, generate recommendations
      setLoading(true);
      try {
        const recs = await getRecommendations(newAnswers, shop.shop_id, recipes);
        setRecommendations(recs);
        // Skip step 3 (recommendations list) and go directly to the first recommendation modal
        if (recs.length > 0) {
          setSelectedDrink(recs[0]);
        }
        setStep(3);
      } catch (error) {
        console.error('Failed to get recommendations:', error);
        alert('Failed to get recommendations. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOrder = async () => {
    const finalUserName = userName.trim() || guestName.trim();
    if (!selectedDrink || !finalUserName) {
      alert('Please enter your name first!');
      return;
    }
    
    try {
      setIsSubmitting(true);
      if (onOrderSubmit) {
        const result = await onOrderSubmit({
          recipeId: selectedDrink.recipe.id,
          targetShopId: shop.shop_id,
          name: finalUserName
        });
        if (result && typeof result === 'object') {
          const displayId = (result as any).displayId || (result as any)?.data?.displayId;
          if (displayId) setOrderDisplayId(String(displayId));
        }
      }
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Failed to submit order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestart = () => {
    setStep(1);
    setCurrentQuestion(0);
    setGuestName('');
    setAnswers({});
    setRecommendations([]);
    setSelectedDrink(null);
    setUserName('');
    setIsSubmitted(false);
    setOrderDisplayId('');
    setShowPreparation(false);
  };

  const handleNextRecommendation = () => {
    if (selectedDrink && recommendations.length > 0) {
      const currentIndex = recommendations.findIndex(rec => rec.recipe.id === selectedDrink.recipe.id);
      const nextIndex = (currentIndex + 1) % recommendations.length;
      setSelectedDrink(recommendations[nextIndex]);
    }
  };

  const handlePrevRecommendation = () => {
    if (selectedDrink && recommendations.length > 0) {
      const currentIndex = recommendations.findIndex(rec => rec.recipe.id === selectedDrink.recipe.id);
      const prevIndex = currentIndex === 0 ? recommendations.length - 1 : currentIndex - 1;
      setSelectedDrink(recommendations[prevIndex]);
    }
  };

  useEffect(() => {
    setIsSubmitting(false);
  }, [isSubmitting]);

  if (questionsLoading) {
    return (
      <div className="ez-pairing-experience">
        <div className="loading-overlay">
          <div className="loading-spinner">Loading questions...</div>
        </div>
      </div>
    );
  }

  if (questionsError) {
    return (
      <div className="ez-pairing-experience">
        <div className="error-card">
          <h2>Error</h2>
          <p>{questionsError}</p>
          <button onClick={() => setReloadToken(prev => prev + 1)}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {};
  if (appearance?.backgroundUrl) {
    containerStyle.backgroundImage = `url(${appearance.backgroundUrl})`;
    containerStyle.backgroundSize = 'cover';
    containerStyle.backgroundPosition = 'center';
  }

  return (
    <div className="ez-pairing-experience" style={containerStyle}>
      {/* Step 1: Name Entry */}
      {step === 1 && (
        <div className="name-entry-card">
          <div className={`company-logo-wrapper fixed-logo-box ${appearance?.logoGradient ? 'gradient-enabled' : ''}`}>
            <img
              src={appearance?.logoUrl || '/company-logo.svg'}
              alt="Company Logo"
              className="company-logo"
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                const src = img.src || '';
                if (src && !src.includes('/api/uploads/proxy')) {
                  img.src = `/api/uploads/proxy?url=${encodeURIComponent(src)}`;
                }
              }}
            />
          </div>
          <h1 className="title">
            {(appearance?.titleText || 'Welcome to EZ Pairing!').split('\n').map((line, idx) => (
              <React.Fragment key={idx}>{line}{idx < ((appearance?.titleText || 'Welcome to EZ Pairing!').split('\n').length - 1) ? <br/> : null}</React.Fragment>
            ))}
          </h1>
          <p className="subtitle">
            {questions.length || 'A Few'} questions, and we know what you would like to drink
            <br />
            <span style={{ display: 'block', marginTop: 6 }}>讓我們協助你尋找最適合的飲品</span>
          </p>
          {demoSettings?.demoMode ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
              <button
                className="start-button"
                onClick={() => {
                  setGuestName(demoSettings.demoName || 'Demo Guest');
                  setStep(2);
                }}
              >
                Start Quiz
              </button>
            </div>
          ) : (
            <>
              <div className="name-input">
                <input
                  id="guest-name"
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Enter your name"
                />
              </div>
              <button
                className="start-button"
                onClick={() => setStep(2)}
                disabled={!guestName.trim()}
              >
                Start Quiz
              </button>
              <button
                className="guest-button"
                onClick={() => {
                  setGuestName('Guest');
                  setStep(2);
                }}
              >
                Start as Guest
              </button>
            </>
          )}
        </div>
      )}

      {/* Step 2: Questions */}
      {step === 2 && questions.length > 0 && (
        <div className="question-card">
          <div className="question-header">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }} />
            </div>
            <div className="question-number">Question {currentQuestion + 1} of {questions.length}</div>
            <h2 className="question-text">{questions[currentQuestion].question}</h2>
            <p className="question-text-en">{questions[currentQuestion].questionEn}</p>
          </div>
          <div className="choices-container">
            <div className={`choices-grid ${questions[currentQuestion].choices.length === 2 ? 'choices-grid-two' : ''}`}>
              {questions[currentQuestion].choices.map((choice) => (
                <button
                  key={choice.id}
                  className="choice-button"
                  onClick={() => handleAnswerSelect(choice.id)}
                  disabled={loading}
                >
                  <div className="choice-text">
                    {choice.emoji && <span className="choice-emoji">{choice.emoji}</span>}
                    {choice.text}
                  </div>
                  <div className="choice-text-en">{choice.textEn}</div>
                </button>
              ))}
            </div>
          </div>
          {loading && (
            <div className="loading-overlay">
              <div className="loading-spinner">Getting your recommendations...</div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Recommendations */}
      {step === 3 && (
        <div className="recommendations-card">
          <div className="recommendations-header">
            <h2 className="recommendations-title">We think you'll like these</h2>
            <h2 className="recommendations-title">呢杯實啱你</h2>
            <p>Served by XOXO Beverages</p>
          </div>
          <div className="recommendations-content">
            <div className="recommendations-list">
              {recommendations.map((rec, index) => (
                <div
                  key={index}
                  className="recommendation-item"
                  onClick={() => setSelectedDrink(rec)}
                >
                  {/* 1. Drink Name */}
                  <h3 className="drink-name">{rec.recipe.name}</h3>
                  
                  {/* 2. Drink Image */}
                  <div className="drink-image">
                    {(() => {
                      const imageUrl = rec.recipe.recipeImage?.imageUrl || rec.recipe.image;
                      return imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={rec.recipe.name}
                          onError={(e) => {
                            const target = e.currentTarget;
                            const placeholder = target.parentElement?.querySelector('.image-placeholder') as HTMLElement;
                            if (placeholder) {
                              target.style.display = 'none';
                              placeholder.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null;
                    })()}
                    <div
                      className="image-placeholder"
                      style={{ display: (rec.recipe.recipeImage?.imageUrl || rec.recipe.image) ? 'none' : 'flex' }}
                    >
                      No Image
                    </div>
                  </div>
                  
                  {/* 3. Ingredients List EN */}
                  <div className="drink-ingredients-en">
                    {(() => {
                      const { english } = getRecipeIngredientNamesWithTranslations(rec.recipe, 'zh-HK');
                      const englishPreview = english.slice(0, 3).join(', ');
                      const ellipsis = english.length > 3 ? '...' : '';
                      return `Ingredients: ${englishPreview}${ellipsis}`;
                    })()}
                  </div>
                  
                  {/* 4. Ingredients List ZH-HK */}
                  <div className="drink-ingredients-zh">
                    {(() => {
                      const { translated } = getRecipeIngredientNamesWithTranslations(rec.recipe, 'zh-HK');
                      const translatedPreview = translated.slice(0, 3).join(', ');
                      const ellipsis = translated.length > 3 ? '...' : '';
                      return `成分: ${translatedPreview}${ellipsis}`;
                    })()}
                  </div>
                  
                  {/* 5. Explanation EN */}
                  {(rec.explanationEn || rec.explanation) && (
                    <div className="drink-explanation-en">
                      {rec.explanationEn || rec.explanation}
                    </div>
                  )}
                  
                  {/* 6. Explanation ZH-HK */}
                  {rec.explanationZh && (
                    <div className="drink-explanation-zh">
                      {rec.explanationZh}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="recommendations-footer">
            <button className="restart-button" onClick={handleRestart}>
              Start Over
            </button>
          </div>
        </div>
      )}

      {/* Drink Selection Modal */}
      {selectedDrink && !showPreparation && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-drink-info">
              {/* Upper Section */}
              <div className="modal-upper">
                <div className="modal-meta">
                  <h3 className="modal-drink-name">{selectedDrink.recipe.name}</h3>
                  <p className="modal-glass">Served by XOXO Beverages</p>
                </div>
                <div className="modal-image">
                  {(() => {
                    const imageUrl = selectedDrink.recipe.recipeImage?.imageUrl || selectedDrink.recipe.image;
                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={selectedDrink.recipe.name}
                        onError={(e) => {
                          const target = e.currentTarget;
                          const placeholder = target.parentElement?.querySelector('.image-placeholder') as HTMLElement;
                          if (placeholder) {
                            target.style.display = 'none';
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null;
                  })()}
                  <div
                    className="image-placeholder"
                    style={{ display: (selectedDrink.recipe.recipeImage?.imageUrl || selectedDrink.recipe.image) ? 'none' : 'flex' }}
                  >
                    No Image
                  </div>
                </div>
                {/* Enhanced Ingredients List */}
                {(() => {
                  const { english, translated } = getRecipeIngredientNamesWithTranslations(selectedDrink.recipe, 'zh-HK');
                  return english.length > 0 ? (
                    <div className="modal-ingredients-enhanced">
                      <h4>Ingredients:</h4>
                      <p style={{ color: '#d1d5db', margin: '0 0 8px 0' }}>{english.join(', ')}</p>
                      <h4 style={{ fontSize: '14px', color: '#d1d5db', margin: '0 0 4px 0' }}>成分:</h4>
                      <p style={{ color: '#a0a0a0', margin: 0, fontSize: '14px' }}>{translated.join(', ')}</p>
                    </div>
                  ) : (
                    <div className="modal-ingredients-enhanced">
                      <h4>Ingredients:</h4>
                      <div className="ingredients-fallback">
                        <p>Ingredient information not available</p>
                      </div>
                    </div>
                  );
                })()}
                {(selectedDrink.explanationEn || selectedDrink.explanation || selectedDrink.explanationZh) && (
                  <div className="modal-explanation">
                    <h4>Why this drink matches you:</h4>
                    <h4>呢杯點解最啱你:</h4>
                    {(selectedDrink.explanationEn || selectedDrink.explanation) && (
                      <p style={{ marginBottom: selectedDrink.explanationZh ? '12px' : '0' }}>
                        {selectedDrink.explanationEn || selectedDrink.explanation}
                      </p>
                    )}
                    {selectedDrink.explanationZh && (
                      <p style={{ fontSize: 14, margin: 0 }}>{selectedDrink.explanationZh}</p>
                    )}
                  </div>
                )}
              </div>
              {/* Lower Section with Radar Chart */}
              <div className="modal-lower">
                <div className="modal-radar">
                  {(() => {
                    const feats = selectedDrink.recipe.drinkFeatures || [];
                    
                    if (!feats.length) {
                      return <div className="radar-placeholder">No flavor profile available</div>;
                    }
                    
                    // Emoji mapping for flavor characteristics
                    const emojiMap: Record<string, string> = {
                      'strength': '💪',
                      'acidity': '🍋',
                      'sweet': '🍯',
                      'spicy': '🌶️',
                      'fruity': '🍓',
                      'fizzy': '✨',
                      'herbal': '🌿'
                    };
                    
                    const picked = feats.slice(0, 7);
                    
                    // Create values object with lowercase keys to match actual data
                    const values = picked.reduce<Record<string, number>>((acc: any, f: any) => {
                      const v = Number(f.val || f.value || f.score || 0);
                      const key = f.property.toLowerCase();
                      acc[key] = Math.max(0, Math.min(5, isFinite(v) ? v : 0));
                      return acc;
                    }, {});
                    
                    // Create features array with emojis and ensure keys match
                    const features = picked.map((f: any) => {
                      const key = f.property.toLowerCase();
                      return emojiMap[key] || f.property;
                    });
                    
                    // Ensure values object uses the same keys as features
                    const finalValues: Record<string, number> = {};
                    features.forEach((feature, index) => {
                      const originalKey = picked[index].property.toLowerCase();
                      finalValues[feature] = values[originalKey] || 0;
                    });
                    
                    return (
                      <div className="radar-container">
                        <h4 className="radar-title">Flavor Profile</h4>
                        <RadarChart
                          features={features}
                          values={finalValues}
                          size={200}
                          radius={70}
                          levels={5}
                          max={5}
                          svgClassName="radar-svg"
                          labelClassName="radar-label"
                          strokeColor="#10b981"
                          fillColor="rgba(16, 185, 129, 0.22)"
                        />
                      </div>
                    );
                  })()}
                </div>
                {/* Order Section */}
                {/* Navigation buttons for multiple recommendations */}
                {recommendations.length > 1 && (
                  <div className="modal-navigation">
                    <button className="nav-button prev-button" onClick={handlePrevRecommendation}>
                      ← Previous
                    </button>
                    <span className="recommendation-counter">
                      {recommendations.findIndex(rec => rec.recipe.id === selectedDrink.recipe.id) + 1} of {recommendations.length}
                    </span>
                    <button className="nav-button next-button" onClick={handleNextRecommendation}>
                      Next →
                    </button>
                  </div>
                )}

                <div className="modal-order-section">
                  {!guestName.trim() && !isSubmitted && (
                    <div className="name-input">
                      <label htmlFor="modal-user-name">Your name (for the order):</label>
                      <input
                        id="modal-user-name"
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        placeholder="Enter your name"
                      />
                    </div>
                  )}
                  {!isSubmitted && guestName.trim() && (
                    <div className="guest-name-display">
                      <p>Ordering as: <strong>{guestName}</strong></p>
                    </div>
                  )}
                  {isSubmitted && (
                    <div className="success-message">
                      {orderDisplayId ? (
                        <p>Your order has been placed! Display ID: <strong>{orderDisplayId}</strong></p>
                      ) : (
                        <p>Your order has been placed! Please wait for your display ID.</p>
                      )}
                    </div>
                  )}
                  <div className="modal-action-buttons">
                    <button
                      className={`modal-order-button ${isSubmitted ? 'disabled' : ''}`}
                      onClick={() => {
                        if (isSubmitted) return; // Prevent action when submitted
                        if (demoSettings?.demoMode) {
                          // Skip preparation modal in demo mode
                          handleOrder();
                        } else {
                          setShowPreparation(true);
                        }
                      }}
                      disabled={isSubmitted || isSubmitting || (!userName.trim() && !guestName.trim())}
                    >
                      Order This Drink
                    </button>
                    <button className={`modal-restart-button ${isSubmitted ? 'active' : ''}`} onClick={handleRestart}>
                      Start Over
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Glass Preparation Modal */}
      {selectedDrink && showPreparation && (
        <div className="modal-overlay" onClick={() => setShowPreparation(false)}>
          <div className="modal-content preparation-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPreparation(false)}>×</button>
            <div className="preparation-content">
              <div className="preparation-header">
                <h2>Prepare Your Glass</h2>
                <p>Follow these steps to get ready for your drink</p>
              </div>
              
              <div className="glass-info">
                <div className="glass-icon">🥤</div>
                <h3>{selectedDrink.recipe.glass?.name || 'Standard Glass'}</h3>
              </div>
              
              <div className="preparation-steps">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-text">
                    <strong>Take a {selectedDrink.recipe.glass?.name || 'clean glass'}</strong>
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">2</div>
                  <div className="step-text">
                    <strong>Fill with ice cubes</strong>
                    <p>Fill the glass about ¾ full with ice</p>
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">3</div>
                  <div className="step-text">
                    <strong>Place on the dispenser base</strong>
                    <p>Position your glass under the dispenser nozzle</p>
                  </div>
                </div>
              </div>
              
              <div className="preparation-actions">
                <button 
                  className="preparation-back-btn"
                  onClick={() => setShowPreparation(false)}
                >
                  Back
                </button>
                <button 
                  className="preparation-ready-btn"
                  onClick={() => {
                    setShowPreparation(false);
                    handleOrder();
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Placing Order...' : 'Ready - Place Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EzPairingExperience;
